import React from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "react-i18next";
import { Recommendation } from "../../types/control";
import {
  formatCurrency,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import { ApplyStatusChip, directionColor } from "./controlPresentation";
import RecommendationActions from "./RecommendationActions";

interface RecommendationCardProps {
  recommendation: Recommendation;
  busy: boolean;
  onAccept: (note?: string) => void | Promise<void>;
  onReject: (note?: string) => void | Promise<void>;
  onRetry: () => void | Promise<void>;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation: r,
  busy,
  onAccept,
  onReject,
  onRetry,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dirColor = directionColor(theme)[r.direction];

  // El % de variación: rojo cuando sube la inversión, verde cuando baja.
  const variationColor =
    r.direction === "INCREASE"
      ? theme.palette.success.main
      : theme.palette.error.main;
  const variationSign = r.variationPercent > 0 ? "+" : "";

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 240, flex: 1 }}>
          {/* 1) Cliente — cabecera destacada */}
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {r.companyName}
          </Typography>

          {/* 2) Línea */}
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            {r.lineName}
          </Typography>
          <Typography variant="body2" color={theme.palette.neutral[600]}>
            {r.insertionOrderName}
          </Typography>

          {/* 3) Briefing (clicable → detalle del briefing) */}
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("control.card.briefing")}:{" "}
            {r.briefingId ? (
              <Link
                href={`/briefings/${r.briefingId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ verticalAlign: "baseline" }}
              >
                {r.briefingName}
              </Link>
            ) : (
              r.briefingName
            )}
          </Typography>
          <br />

          {/* 3b) Configuración de la línea (associated campaign) */}
          {r.associatedCampaignId && r.briefingId && (
            <>
              <Typography variant="caption" color={theme.palette.neutral[600]}>
                <Link
                  href={`/briefings/${r.briefingId}/associated-campaigns/${r.associatedCampaignId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ verticalAlign: "baseline" }}
                >
                  {t("control.card.lineConfigLink")}
                </Link>
              </Typography>
              <br />
            </>
          )}

          {/* 4) Source ID */}
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("control.card.sourceId")}: {r.sourceId}
          </Typography>

          {/* 5) Objetivo (badge) · 6) Campaign type · 7) Campaign format */}
          <Box
            sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1, mb: 0.5 }}
          >
            {r.objective && (
              <Tooltip title={t("control.card.objective")}>
                <Chip
                  label={r.objective}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {r.briefingCampaignType && (
              <Tooltip title={t("control.card.campaignType")}>
                <Chip label={r.briefingCampaignType} size="small" />
              </Tooltip>
            )}
            {r.campaignFormat && (
              <Tooltip title={t("control.card.campaignFormat")}>
                <Chip label={r.campaignFormat} size="small" />
              </Tooltip>
            )}
          </Box>

          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("control.card.campaign")}: {r.campaignStartDate} –{" "}
            {r.campaignEndDate}
          </Typography>
          <br />

          {/* Fecha de la ejecución que generó/refrescó el aviso (runDate). Es
              "de cuándo son los datos"; no se toca al aceptar/rechazar. */}
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("control.card.generatedAt")}:{" "}
            {new Date(r.runDate).toLocaleDateString("es-ES")}
          </Typography>
        </Box>

        <Stack spacing={1} alignItems="flex-end">
          <Chip
            label={t(`control.direction.${r.direction}`)}
            size="small"
            sx={{
              bgcolor: dirColor,
              color: "white",
              fontWeight: 700,
            }}
          />
          <Chip
            label={`${variationSign}${r.variationPercent}%`}
            sx={{
              bgcolor: variationColor,
              color: "white",
              fontWeight: 700,
              fontSize: "1rem",
            }}
          />
          <ApplyStatusChip applyStatus={r.applyStatus} />
        </Stack>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Inversión actual → sugerida */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("control.card.currentInvestment")}
          </Typography>
          <Typography variant="h6">
            {formatCurrency(r.currentInvestment, r.currency)}
          </Typography>
        </Box>
        <ArrowForwardIcon sx={{ color: theme.palette.neutral[400] }} />
        <Box>
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            {t("control.card.suggestedInvestment")}
          </Typography>
          <Typography variant="h6" sx={{ color: dirColor, fontWeight: 700 }}>
            {formatCurrency(r.suggestedInvestment, r.currency)}
          </Typography>
        </Box>
      </Box>

      {/* Impacto en el margen comercial: actual → resultante */}
      {r.currentMargin != null && r.projectedMargin != null && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color={theme.palette.neutral[600]}>
              {t("control.card.currentMargin")}
            </Typography>
            <Typography variant="h6">
              {formatPercentageNoMultiply(r.currentMargin)}
            </Typography>
          </Box>
          <ArrowForwardIcon sx={{ color: theme.palette.neutral[400] }} />
          <Box>
            <Typography variant="caption" color={theme.palette.neutral[600]}>
              {t("control.card.projectedMargin")}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {formatPercentageNoMultiply(r.projectedMargin)}
            </Typography>
          </Box>
        </Box>
      )}

      {r.cappedByMaxAdjustment && (
        <Tooltip title={t("control.card.cappedTooltip")}>
          <Chip
            icon={<WarningAmberIcon />}
            label={t("control.card.cappedLabel")}
            color="warning"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
        </Tooltip>
      )}

      {/* Explicación legible */}
      <Typography variant="body2" sx={{ mb: 2, whiteSpace: "pre-line" }}>
        {r.reasonText}
      </Typography>

      {/* Resultado de aplicación fallida */}
      {r.applyStatus === "FAILED" && r.applyError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {r.applyError}
        </Alert>
      )}
      {r.applyStatus === "MANUAL_REQUIRED" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("control.card.manualRequiredInfo")}
        </Alert>
      )}

      <RecommendationActions
        recommendation={r}
        busy={busy}
        onAccept={onAccept}
        onReject={onReject}
        onRetry={onRetry}
      />
    </Paper>
  );
};

export default RecommendationCard;
